/**
 * Governance Config Store — Persisted Policy Overrides
 *
 * RESOLVES: CONFLICT-03 (Governance Policy Settings Not Persisted)
 *
 * Stores governance policy overrides in localStorage so that
 * GovernanceSettingsTab customizations survive page reloads.
 *
 * The policyEngine reads from this store at initialization,
 * merging overrides with defaultPolicy.
 */

import { debug, warn } from '../../utils/logger'
import { safeStorageWrite } from '../../utils/storageQuota'
import { safeLocalStorageGet } from '../../utils/logger'

const GOVERNANCE_CONFIG_KEY = 'meester_djay_governance_config_v1'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GovernanceConfigOverrides {
  /** Schema version for migration safety */
  version: 1

  /** Quality gate overrides */
  qualityGates?: {
    minCogLevel?: number
    maxCogLevel?: number
    minDidacticDepth?: number
    minQualityScore?: number
  }

  /** Pipeline stage toggles */
  pipelineStages?: {
    schemaEnabled?: boolean
    typeEnabled?: boolean
    contentEnabled?: boolean
    supportEnabled?: boolean
    visualEnabled?: boolean
    graphEnabled?: boolean
  }

  /** Pipeline behavior */
  pipelineBehavior?: {
    strictMode?: boolean
    autoBlock?: boolean
  }

  /** Policy overrides */
  policy?: {
    minQuestionsPerSkill?: number
    maxQuestionsPerSkill?: number
    requireHints?: boolean
    requireExplanation?: boolean
    requireVisuals?: boolean
    distributionCheck?: boolean
  }

  /** Notification preferences */
  notifications?: {
    notifyBlockers?: boolean
    notifyWarnings?: boolean
    notifyWeeklyReport?: boolean
    notifyNewRules?: boolean
  }

  /** System settings */
  system?: {
    cacheEnabled?: boolean
    debugMode?: boolean
    autoValidateOnSave?: boolean
    batchSize?: number
  }

  /** Timestamp of last save */
  lastSaved?: number
}

// ─── Default Config ───────────────────────────────────────────────────────────

const DEFAULT_CONFIG: GovernanceConfigOverrides = {
  version: 1,
  qualityGates: {
    minCogLevel: 2,
    maxCogLevel: 5,
    minDidacticDepth: 2,
    minQualityScore: 70,
  },
  pipelineStages: {
    schemaEnabled: true,
    typeEnabled: true,
    contentEnabled: true,
    supportEnabled: true,
    visualEnabled: true,
    graphEnabled: true,
  },
  pipelineBehavior: {
    strictMode: false,
    autoBlock: true,
  },
  policy: {
    minQuestionsPerSkill: 5,
    maxQuestionsPerSkill: 50,
    requireHints: true,
    requireExplanation: true,
    requireVisuals: false,
    distributionCheck: true,
  },
  notifications: {
    notifyBlockers: true,
    notifyWarnings: false,
    notifyWeeklyReport: true,
    notifyNewRules: true,
  },
  system: {
    cacheEnabled: true,
    debugMode: false,
    autoValidateOnSave: true,
    batchSize: 100,
  },
}

// ─── Store Operations ─────────────────────────────────────────────────────────

/**
 * Load governance config from localStorage.
 * Returns default config if nothing is stored or if stored data is invalid.
 */
export function loadGovernanceConfig(): GovernanceConfigOverrides {
  try {
    const raw = localStorage.getItem(GOVERNANCE_CONFIG_KEY)
    if (!raw) return { ...DEFAULT_CONFIG }

    const parsed = JSON.parse(raw)

    // Version check
    if (!parsed || parsed.version !== 1) {
      warn('Governance config version mismatch, using defaults')
      return { ...DEFAULT_CONFIG }
    }

    // Merge with defaults to fill any missing fields
    return mergeWithDefaults(parsed)
  } catch (e) {
    warn('Failed to load governance config, using defaults')
    return { ...DEFAULT_CONFIG }
  }
}

/**
 * Save governance config to localStorage.
 */
export function saveGovernanceConfig(
  config: GovernanceConfigOverrides,
): boolean {
  try {
    const toSave: GovernanceConfigOverrides = {
      ...config,
      version: 1,
      lastSaved: Date.now(),
    }
    const success = safeStorageWrite(
      GOVERNANCE_CONFIG_KEY,
      JSON.stringify(toSave),
    )
    if (success) {
      debug('✅ Governance config saved')
    }
    return success !== false
  } catch (e) {
    warn('Failed to save governance config')
    return false
  }
}

/**
 * Reset governance config to defaults.
 */
export function resetGovernanceConfig(): GovernanceConfigOverrides {
  localStorage.removeItem(GOVERNANCE_CONFIG_KEY)
  debug('🔄 Governance config reset to defaults')
  return { ...DEFAULT_CONFIG }
}

/**
 * Get the default config (for reset operations).
 */
export function getDefaultGovernanceConfig(): GovernanceConfigOverrides {
  return { ...DEFAULT_CONFIG }
}

/**
 * Get the list of disabled pipeline stages from config.
 * Returns stage numbers (1-6) that should be skipped.
 */
export function getDisabledStages(config: GovernanceConfigOverrides): number[] {
  const stages = config.pipelineStages
  if (!stages) return []

  const disabled: number[] = []
  if (stages.schemaEnabled === false) disabled.push(1)
  if (stages.graphEnabled === false) disabled.push(2)
  if (stages.contentEnabled === false) disabled.push(3)
  if (stages.visualEnabled === false) disabled.push(4)
  if (stages.supportEnabled === false) disabled.push(5)
  // Type validation is part of stage 1
  if (stages.typeEnabled === false) disabled.push(1)

  return [...new Set(disabled)]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mergeWithDefaults(
  stored: Partial<GovernanceConfigOverrides>,
): GovernanceConfigOverrides {
  return {
    version: 1,
    qualityGates: {
      ...DEFAULT_CONFIG.qualityGates,
      ...stored.qualityGates,
    },
    pipelineStages: {
      ...DEFAULT_CONFIG.pipelineStages,
      ...stored.pipelineStages,
    },
    pipelineBehavior: {
      ...DEFAULT_CONFIG.pipelineBehavior,
      ...stored.pipelineBehavior,
    },
    policy: {
      ...DEFAULT_CONFIG.policy,
      ...stored.policy,
    },
    notifications: {
      ...DEFAULT_CONFIG.notifications,
      ...stored.notifications,
    },
    system: {
      ...DEFAULT_CONFIG.system,
      ...stored.system,
    },
    lastSaved: stored.lastSaved,
  }
}
